// "More actions" overflow popover.
//
// Rendered through a portal so the row's stacking context (grids,
// transforms) can't clip the menu. Portals through `usePortalRoot()`
// — the embed root inside `<EmbeddableChat>`, `document.body`
// otherwise — so the menu stays inside the embed's bounding box when
// embedded. Copy text / copy link / report are the current items; the
// parent row passes real handlers as the actions land in subsequent
// tickets.
//
// We deliberately don't claim `role="menu"` — that role promises
// arrow-key navigation between `menuitem` children, which we don't
// implement today. A popover of plain `<button>` elements is honest,
// and screen readers treat them as reachable actions without forcing
// "application mode".

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { usePortalRoot } from "~/components/EmbedPortalContext";
import { usePortalPlacement } from "~/lib/portal-positioning";

interface MoreMenuProps {
  anchor: HTMLElement | null;
  onClose: () => void;
  /**
   * Optional handlers. A missing handler renders a disabled button so
   * the popover retains its shape while the action awaits its own
   * ticket.
   */
  onCopyText?: () => void;
  onCopyLink?: () => void;
  onReport?: () => void;
}

/** Nominal menu footprint used for overflow clamping. If future
 *  content pushes the menu past these numbers the viewport clamp
 *  still applies; it just loses a hair of precision. */
const MENU_WIDTH_HINT = 160;
const MENU_HEIGHT_HINT = 128;
const VIEWPORT_EDGE = 8;
const ANCHOR_OFFSET = 4;

export function MoreMenu({ anchor, onClose, onCopyText, onCopyLink, onReport }: MoreMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const portalRoot = usePortalRoot();

  // Flip vertically when the menu would clip the surface bottom; clamp
  // horizontally so it never hangs off the right edge. Surface
  // dimensions come from `getPortalViewportSize` — the viewport in
  // standalone, the embed root's box when embedded — so a popover
  // triggered inside a 600px-wide embed stays inside that 600px box
  // rather than spilling out into the host page.
  const placement = usePortalPlacement({
    anchor,
    portalRoot,
    computeViewportCoords: (rect, surface) => {
      const surfaceBottom = surface.offsetTop + surface.height;
      const surfaceRight = surface.offsetLeft + surface.width;
      const spaceBelow = surfaceBottom - rect.bottom;
      const flipsUp = spaceBelow < MENU_HEIGHT_HINT + ANCHOR_OFFSET;
      const viewportTop = flipsUp
        ? Math.max(surface.offsetTop + VIEWPORT_EDGE, rect.top - MENU_HEIGHT_HINT - ANCHOR_OFFSET)
        : rect.bottom + ANCHOR_OFFSET;
      const maxLeft = surfaceRight - MENU_WIDTH_HINT - VIEWPORT_EDGE;
      const viewportLeft = Math.max(
        surface.offsetLeft + VIEWPORT_EDGE,
        Math.min(rect.left, maxLeft),
      );
      return { viewportTop, viewportLeft };
    },
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        anchor?.focus?.();
      }
    }
    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node;
      // Close only when the click lands outside both the menu *and*
      // its trigger. Checking only the trigger (the earlier bug)
      // dismissed the menu on every inside-body click before a
      // menuitem could fire.
      if (menuRef.current?.contains(target)) return;
      if (anchor?.contains(target)) return;
      onClose();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [anchor, onClose]);

  if (!anchor || !placement || !portalRoot) return null;

  return createPortal(
    <div
      ref={menuRef}
      aria-label="More actions"
      style={{
        position: placement.position,
        top: placement.top,
        left: placement.left,
        zIndex: 50,
        minWidth: MENU_WIDTH_HINT,
        padding: "var(--chat-space-1) 0",
        borderRadius: "var(--chat-radius-xl)",
        border: "1px solid var(--chat-surface-border)",
        background: "var(--chat-surface-overlay)",
        boxShadow: "var(--chat-shadow-popover)",
      }}
    >
      <MenuItem label="Copy text" onClick={onCopyText} />
      <MenuItem label="Copy link" onClick={onCopyLink} />
      <MenuItem label="Report" onClick={onReport} />
    </div>,
    portalRoot,
  );
}

function MenuItem({ label, onClick }: { label: string; onClick?: () => void }) {
  return (
    <button
      type="button"
      className="chat-button"
      onClick={onClick}
      disabled={!onClick}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        padding: "var(--chat-space-2) var(--chat-space-3)",
        border: "none",
        background: "transparent",
        color: onClick ? "var(--chat-fg-primary)" : "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
        cursor: onClick ? "pointer" : "not-allowed",
      }}
    >
      {label}
    </button>
  );
}
